import { TravelerForm } from '../traveler/traveler.types';
import {
    BookingStatus,
    type PaymentRequirementsData,
    type DiscountData,
    type FlatPricingData,
    type AgebandPricingData,
    DiscountType,
    type GetProductsResItem0RouteItemDto,
    type NoteVisibility,
    EventStatus,
    GetTeamsResItemMembersItem0ReferenceDto,
} from '../../generated';
import {
    Activity,
    Period,
    SourceType,
    Currency,
    PricingType,
    PaxData,
} from '../common.types';
import { ProductType, ProductOptionType } from '../../generated';
import { Customer } from '../customer/customer.types';

// re-exporting enums
export { BookingStatus };

export interface BookingProductPricing {
    id: string;
    type: PricingType;
    currency: Currency;
    cost?: FlatPricingData | AgebandPricingData;
    price: FlatPricingData | AgebandPricingData;
    paymentRequirements: PaymentRequirementsData;
    inclusions?: string[];
    exclusions?: string[];
    discount?: DiscountData;
}

export interface BookingProduct {
    id: string;
    type: ProductType;
    name: string;
    pricing: BookingProductPricing;
    options?: BookingProductOption[];
}

export interface BookingParticipants {
    pax: PaxData;
    customer: Customer;
    travelers?: TravelerForm[];
}

export interface BookingNote {
    id?: string;
    content: string;
    visibility: NoteVisibility;
    createByCustomer?: boolean;
}

export interface BookingProductOption {
    id: string;
    pax: PaxData;
    pricing?: BookingProductPricing;
    name: string;
    type: ProductOptionType;
}

export interface BookingFinancials {
    debt: number;
    cost: number;
    subtotal: number;
    total: number;
    paid: number;
    refunded: number;
    waived: number;
}

export interface BookingRoute {
    items: (GetProductsResItem0RouteItemDto & { time?: Date })[];
    round_trip: boolean;
}

export interface Booking {
    id: string;
    capacityId?: string;
    status: BookingStatus;
    customerBookingsCount: number;
    eventStatus?: EventStatus;
    createdAt: Date;
    createdBy: string;
    lifecycle: Activity[];
    reason?: string;
    notes: BookingNote[];
    orderId: string;
    orderNumber: string;
    eventId?: string;
    date: Period;
    route?: BookingRoute;
    transactions: Transaction[];
    source: {
        type: SourceType;
        name: string;
        reference: GetTeamsResItemMembersItem0ReferenceDto;
    };
    product: BookingProduct;
    financials: BookingFinancials;
    participants: BookingParticipants;
    permissions: {
        canEdit: boolean;
        canDelete: boolean;
    };
    shared?: boolean;
}

export interface Transaction {
    id: string;
    operator: string;
    type: string;
    amount: number;
    method: string;
    createdDate: Date;
    discarded?: Activity;
}

export enum PaymentStatus {
    PAID = 'paid',
    NOT_PAID = 'not_paid',
}

export interface BookingPriceParams {
    productId: string;
    time: number;
    capacityId: string;
    pax: PaxData;
    currency: string;
    price: FlatPricingData | AgebandPricingData;
    options: {
        id: string;
        name: string;
        type: string;
        pax: PaxData;
        price?: FlatPricingData | AgebandPricingData;
    }[];
    discount?: {
        amount: number;
        type: DiscountType;
    };
    customerId?: string;
}

export interface BookingPriceSummary {
    currency: string;
    final: number;
    beforeDiscount: number;
    discount?: {
        amount: number;
        type: DiscountType;
    };
    pax: PaxData;
    price?: FlatPricingData | AgebandPricingData;
    options?: {
        name: string;
        type: string;
        pax: PaxData;
        price?: FlatPricingData | AgebandPricingData;
    }[];
}

export interface UseBookingsLazyData {
    order_number?: string;
    types?: ProductType[];
    statuses?: BookingStatus[];
    action?: 'created' | 'last_updated' | 'deleted';
    at_start?: number;
    at_end?: number;
    by?: string;
    sourceType?: SourceType;
    sourceName?: string;
    sourceEmail?: string;
    text_search?: string;
    startTs?: number;
    endTs?: number;
    pageNumber?: number;
    pageSize?: number;
}
